import { ExpandMore } from "@mui/icons-material";
import { Box, Button, CardActions, FormLabel, MenuItem, Typography, useTheme } from "@mui/material";
import { useFieldArray, useFormContext } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { DateRangeType } from "../../../common/enums/DRCXEnums";
import { ReportScheduleFormType, ScheduledReportsFormType, daysOfWeek } from "../ScheduledReportsEditorUtils";
import { useMemo, type JSX } from "react";
import { useQueryNotifications } from "../../../queries/UseQueryNotifications";
import { useTimezones } from "../../../queries/time-zones/UseTimezones";
import { isMobile, isTablet } from "react-device-detect";
import FormCheckbox from "../../forms/form-checkbox/FormCheckbox";
import FormDateTimePicker from "../../forms/form-date-time-picker/FormDateTimePicker";
import FormInput from "../../forms/form-input/FormInput";
import FormPropertySection from "../../forms/form-property-section/FormPropertySection";
import FormProperty from "../../forms/form-property/FormProperty";
import FormRadioGroup, { FormRadioGroupRadioButton } from "../../forms/form-radio-group/FormRadioGroup";
import FormSelect from "../../forms/form-select/FormSelect";
import StepMenuLandingCard from "../../step-menu/StepMenuLandingCard";
import StepMenuLandingCardDetails from "../../step-menu/StepMenuLandingCardDetails";
import StepMenuLandingCardHeader from "../../step-menu/StepMenuLandingCardHeader";

export type ScheduleCardProps = {

    item: ReportScheduleFormType;
    duplicateEnabled?: boolean;
    deleteEnabled?: boolean;
    renameEnabled?: boolean;

    onDuplicate?: (item: ReportScheduleFormType) => void;
    onDelete?: (item: ReportScheduleFormType) => void;
    onRename?: (item: ReportScheduleFormType) => void;
};

export default function ScheduleCard({
    item,
    duplicateEnabled,
    deleteEnabled,
    renameEnabled,
    onDuplicate,
    onDelete,
    onRename,
}: ScheduleCardProps): JSX.Element {

    const isPhone = isMobile && !isTablet;
    const { t } = useTranslation();
    const theme = useTheme();
    const { data: timezones } = useQueryNotifications(useTimezones)();

    const {
        getValues,
        watch,
        //trigger,
        control,
        //getValues,
        //reset,
        setValue,
        //resetField
    } = useFormContext<ScheduledReportsFormType>();    


    const { fields: list,
        // remove, 
        // append 
        } = useFieldArray({
            control,
            name: 'schedules',
            keyName: 'fieldKey'
          });

   
    
    // we need the 'real' index of the item
    const index = list.findIndex(o => o.id === item.id);

    const [
        description,
        //repeatRange,
        expanded,
        //repeatInterval,
        frequencyOption

    ] = watch([
        `schedules.${index}.description`,
        //`schedules.${index}.repeatRange`,
        `schedules.${index}.expanded`,
        //`schedules.${index}.repeatInterval`,
        `schedules.${index}.frequencyOption`
    ]);


    const frequencyRadioButtons: FormRadioGroupRadioButton[] = useMemo(() => [{
        label: t('scheduledReports.frequency.monthly'),
        ariaLabel: t('scheduledReports.frequency.monthly') as string,
        value: DateRangeType.Month
    }, {
        label: t('scheduledReports.frequency.weekly'),
        ariaLabel: t('scheduledReports.frequency.weekly') as string,
        value: DateRangeType.Week
    }, {
        label: t('scheduledReports.frequency.daily'),
        ariaLabel: t('scheduledReports.frequency.daily') as string,
        value: DateRangeType.Day
    }, {
        label: t('scheduledReports.frequency.hourly'),
        ariaLabel: t('scheduledReports.frequency.hourly') as string,
        value: DateRangeType.Hour
    }, {
        label: t('scheduledReports.frequency.custom'),
        ariaLabel: t('scheduledReports.frequency.custom') as string,
        value: DateRangeType.Raw
    }], [
        t
    ]);

    const frequencySelect: FormRadioGroupRadioButton[] = useMemo(() => [{
        label: t('scheduledReports.frequency.year'),
        value: DateRangeType.Year
    }, {
        label: t('scheduledReports.frequency.month'),
        value: DateRangeType.Month
    }, {
        label: t('scheduledReports.frequency.week'),
        value: DateRangeType.Week
    }, {
        label: t('scheduledReports.frequency.day'),
        value: DateRangeType.Day
    }, {
        label: t('scheduledReports.frequency.hour'),
        value: DateRangeType.Hour
    }, {
        label: t('scheduledReports.frequency.custom'),
        ariaLabel: t('scheduledReports.frequency.custom') as string,
        value: DateRangeType.Raw
    }], [
        t
    ]);

    function renderTime() {
        return (

            <FormSelect 
                name={`schedules.${index}.minute`}
                label={t('scheduledReports.steps.details.minutes')}
                sx={{
                    minWidth: isPhone ? '60px' : '80px',
                }}
            >

                {
                    Array.from(
                        { length: 60}
                    ).map((_o, ndx) => 

                    <MenuItem key={ndx} value={ndx}>
                        {ndx}
                    </MenuItem>)
                }

            </FormSelect>

    )}

    function renderHourAndTime() {
        return (
            <>
                <FormSelect 
                    name={`schedules.${index}.hour`}
                    label={t('scheduledReports.steps.details.hour')}
                    sx={{
                        minWidth: isPhone ? '60px' : '80px',
                        marginRight: 1
                    }}

                >

                    {
                        Array.from(
                            // Bug 52631: DOC - Carousel Energy Services - Unable to Save Schedule
                            // { length: (24 - 1) / 1 + 1 },
                            // (value, index) => 1 + index * 1
                            { length: 24 },
                            (value, index) => index
                        ).map((o, ndx) => 

                        <MenuItem key={ndx} value={o}>
                            {o}
                        </MenuItem>)
                    }

                </FormSelect>

                <FormLabel 
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                    }}
                >
                    :
                </FormLabel>
                <FormSelect 
                    name={`schedules.${index}.minute`}
                    label={t('scheduledReports.steps.details.minute')}
                    sx={{
                        minWidth: isPhone ? '60px' : '80px',
                    }}
                >

                    {
                        Array.from(
                            { length: 60}
                        ).map((_o, ndx) => 

                        <MenuItem key={ndx} value={ndx}>
                            {ndx}
                        </MenuItem>)
                    }

                </FormSelect>
        </>

    )}

    function renderMonthlyFrequency() {
        return (
            <Box key='monthly' sx={{
                display: 'flex'
            }}>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {t('scheduledReports.steps.details.runReportOnDay')}
                </FormLabel>
                <FormSelect 
                    id='dayofmonth'
                    name={`schedules.${index}.dayOfMonth`}
                    label={t('scheduledReports.steps.details.day')}
                    sx={{
                        minWidth: isPhone ? '60px' : '80px',
                        marginRight: 1
                    }}
                >

                    {
                        Array.from(
                            { length: (31 - 1) / 1 + 1 },
                            (_value, index) => 1 + index * 1
                        ).map((o, ndx) => 

                        <MenuItem key={ndx} value={o}>
                            {o}
                        </MenuItem>)
                    }

                </FormSelect>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {`${t('scheduledReports.steps.details.ofEach')} ${t('scheduledReports.steps.details.month')} at`}
                </FormLabel>
                {renderHourAndTime()}
            </Box>
        );}

    function renderDailyFrequency() {
        return (

            <>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {`${t('scheduledReports.steps.details.runReportEvery')} ${t('scheduledReports.steps.details.day')} at`}
                </FormLabel>
                {renderHourAndTime()}

        </>

    )}

    function renderHourlyFrequency() {
        return (

            <>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {`${t('scheduledReports.steps.details.runReportEvery')} ${t('scheduledReports.steps.details.hour')} at`}
                </FormLabel>


                {renderTime()}

                <FormLabel
                    sx={{
                        marginLeft: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {t('scheduledReports.steps.details.pastTheHour')}
                </FormLabel>


        </>

    )}

    function renderWeeklyFrequency() {
        return (
            <Box key='weekly' sx={{
                display: 'flex'
            }}>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    {`${t('scheduledReports.steps.details.runReportEvery')}`}
                </FormLabel>
                <FormSelect 
                    id='dayofweek'
                    name={`schedules.${index}.dayOfWeek`}
                    label={t('scheduledReports.steps.details.day')}
                    sx={{
                        minWidth: isPhone ? '60px' : '80px',
                        marginRight: 1
                    }}
                >

                    {
                        daysOfWeek.map((o, ndx) => 

                        <MenuItem key={ndx} value={ndx}>
                            {o}
                        </MenuItem>)
                    }

                </FormSelect>
                <FormLabel
                    sx={{
                        marginRight: 1,
                        marginTop: 'auto',
                        marginBottom: 'auto'
                }}>
                    at
                </FormLabel>
                {renderHourAndTime()}
            </Box>
        );}

    function renderCustomFrequency() {
        return (

            <>
                <FormDateTimePicker
                    name={`schedules.${index}.startTime`}

                    label={t("datetime.startTime")}
                    sx={{
                            marginTop: 1,
                        }}
                />

                <FormInput
                    name={`schedules.${index}.repeatInterval`}
                        numeric={true}
                        label={t('scheduledReports.repeatInterval')}
                        sx={{
                            marginTop: 1,
                            marginLeft: isPhone ? 0 : 1,
                            marginRight: 1
                        }}
                />

                <FormSelect 
                    name={`schedules.${index}.repeatRange`}
                    label={t('scheduledReports.frequency.label')}
                    sx={{
                        minWidth: isPhone ? '60px' : '80px',
                        marginTop: 1,
                        marginRight: 1
                    }}
                >

                    {
                        frequencySelect.map((o, ndx) => 

                        <MenuItem key={o.value} value={o.value}>
                            {o.label}
                        </MenuItem>)
                    }

                </FormSelect>
        </>

    )}

    function toggleExpanded() {

        setValue(`schedules.${index}.expanded`, !expanded);
    }

    function onFrequencyChanged() {
        const value = getValues(`schedules.${index}.frequencyOption`);

        if (value !== DateRangeType.Raw) {
            setValue(`schedules.${index}.repeatRange`, value, {shouldDirty: true})
            setValue(`schedules.${index}.repeatInterval`, 1, {shouldDirty: true});
        } else {
            setValue(`schedules.${index}.repeatRange`, DateRangeType.Day, {shouldDirty: true})
            setValue(`schedules.${index}.repeatInterval`, 1, {shouldDirty: true});

        }

    }

    return (
        <StepMenuLandingCard>
            <CardActions sx={{ paddingTop: 0, paddingBottom: 0 }}>
                <Typography
                    variant="subtitle2"
                    sx={{
                        textTransform: 'uppercase',
                        fontWeight: 600
                    }}>
                    {description}
                </Typography>

                <Button
                    size="small"
                    sx={{
                        color: theme.palette.grey[600]
                    }}
                    variant="text"
                    onClick={() => toggleExpanded()}
                    style={{ marginLeft: "auto" }}
                    endIcon={<ExpandMore sx={{
                        transform: !expanded ? "rotate(0deg)" : "rotate(180deg)",
                        transition: theme.transitions.create('transform', {
                            duration: theme.transitions.duration.shortest,
                        })
                    }} />}
                >
                </Button> 
            </CardActions>
            <StepMenuLandingCardHeader
                duplicateEnabled={duplicateEnabled}
                deleteEnabled={deleteEnabled}
                renameEnabled={renameEnabled}
                hideEdit={true}
                onDuplicate={onDuplicate ? () => onDuplicate(item) : undefined}
                onDelete={onDelete ? () => onDelete(item) : undefined}
                onRename={onRename ? () => onRename(item) : undefined}

                title={expanded &&
                    <FormInput
                        name={`schedules.${index}.description`}
                        label='Description'
                        sx={{display: 'flex'}}
                    />
                }
            />
            <StepMenuLandingCardDetails
                expanded={expanded}
                hideCollapseButton={true}
                onToggleExpanded={() => {
                    toggleExpanded();
                }}
            >

                <FormPropertySection label=''>
                    <FormProperty>
                        <FormRadioGroup
                            name={`schedules.${index}.frequencyOption`}
                            label={t('scheduledReports.frequency.label')}       
                            direction='row'
                            radioButtons={frequencyRadioButtons} 
                            onChange={onFrequencyChanged}
                        />

                    </FormProperty>
                </FormPropertySection>

                <FormPropertySection label='' >
                    {
                        frequencyOption === DateRangeType.Month ? 
                            renderMonthlyFrequency() :
                        frequencyOption === DateRangeType.Week ? 
                            renderWeeklyFrequency() :
                        frequencyOption === DateRangeType.Day ? 
                            renderDailyFrequency() :
                        frequencyOption === DateRangeType.Hour ? 
                            renderHourlyFrequency() :
                        
                        renderCustomFrequency()
                    }
               
                </FormPropertySection>

                <FormPropertySection label=''>
                        <FormSelect
                            name={`schedules.${index}.timezoneIndex`}
                            label={t("disableUserNotification.timezone")}
                            rules={{
                                required: false
                            }}
                            sx={{
                                marginTop: 1
                            }}
                        >
                            {timezones?.map((o, index) =>
                                <MenuItem key={`tz-${index}`} value={o.id}>
                                    {o.name}
                                </MenuItem>)}
                        </FormSelect>
                </FormPropertySection>

                <FormPropertySection label=''>

                    <FormProperty>

                        <FormCheckbox
                            name={`schedules.${index}.isActive`}
                            ariaLabel={t('scheduledReports.active')}
                            label={t('scheduledReports.active')}
                            sx={{ width: '100%' }}
                        />

                    </FormProperty>

                </FormPropertySection>

            </StepMenuLandingCardDetails>
        </StepMenuLandingCard>
    );
}

